import { z } from "zod";
import { TopolSectionSchema } from "../templateSchema";
import { TopolSpecialBlockTranslationSchema } from "../template/languageSchema";

export const PremadeBlockSchema = z.object({
  name: z.string().optional(),
  img: z.string().optional(),
  definition: z.array(TopolSectionSchema).or(TopolSectionSchema),
  translation: TopolSpecialBlockTranslationSchema.optional().catch(undefined),
});

export const PremadeGroupSchema = z.object({
  id: z.number().optional(),
  name: z.string(),
  blocks: z.array(PremadeBlockSchema),
});

export const PremadeBlocksSchema = z.array(PremadeGroupSchema);

/**
 * Public identifiers for Topol's default premade block groups, in the order
 * the sidebar shows them when nothing reorders them.
 */
export const PRESET_IDS = ["header", "content", "ecommerce", "footer"] as const;

export type IPremadePresetId = (typeof PRESET_IDS)[number];

/**
 * The one description of Topol's default groups: how each public preset id
 * is keyed in the bundled block sets, and which i18n key names its group.
 * Everything that builds a default group reads it from here.
 */
export const PRESET_GROUPS: Record<
  IPremadePresetId,
  { legacyKey: keyof ILegacyPremadeBlocks; nameKey: string }
> = {
  header: { legacyKey: "headers", nameKey: "premade.header" },
  content: { legacyKey: "content", nameKey: "premade.content" },
  ecommerce: { legacyKey: "ecomm", nameKey: "premade.ecommerce" },
  footer: { legacyKey: "footers", nameKey: "premade.footer" },
};

/**
 * A bare reference to one of Topol's default groups. Placed inside the
 * advanced `premadeBlocks.blocks` array it expands, in place, to that group.
 * Strict: any extra key is a validation error.
 */
export const PremadePresetSchema = z
  .object({
    preset: z.enum(PRESET_IDS),
  })
  .strict();

export const PremadeEntriesSchema = z
  .array(PremadeGroupSchema.or(PremadePresetSchema))
  .superRefine((entries, ctx) => {
    const seen = new Set<IPremadePresetId>();
    entries.forEach((entry, index) => {
      if (!("preset" in entry)) return;
      if (seen.has(entry.preset)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [index, "preset"],
          message: `Preset "${entry.preset}" is listed more than once`,
        });
      }
      seen.add(entry.preset);
    });
  });

export const LegacyPremadeBlocksSchema = z.object({
  content: z.array(PremadeBlockSchema).optional(),
  ecomm: z.array(PremadeBlockSchema).optional(),
  footers: z.array(PremadeBlockSchema).optional(),
  headers: z.array(PremadeBlockSchema).optional(),
});

export const PremadeBlocksOverrideSchema = z.object({
  blocks: PremadeEntriesSchema.or(LegacyPremadeBlocksSchema),
  override: z.boolean(),
});

export const OptionsPremadeBlockSchema = PremadeBlocksOverrideSchema.or(
  PremadeBlocksSchema
)
  .or(LegacyPremadeBlocksSchema)
  .or(z.literal(false));

export type IOptionsPremadeBlock = z.infer<typeof OptionsPremadeBlockSchema>;
export type IPremadeBlock = z.infer<typeof PremadeBlockSchema>;
export type IPremadeGroup = z.infer<typeof PremadeGroupSchema>;
export type IPremadeBlocks = z.infer<typeof PremadeBlocksSchema>;
export type IPremadeEntry = z.infer<typeof PremadeEntriesSchema>[number];
export type ILegacyPremadeBlocks = z.infer<typeof LegacyPremadeBlocksSchema>;
export type IPremadeBlocksOverride = z.infer<
  typeof PremadeBlocksOverrideSchema
>;
